import React, { Fragment, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { AllAccountTransactions } from '#components/mobile/accounts/AllAccountTransactions';
import { AyudaGavi } from '#components/mobile/ayuda/AyudaGavi';
import { OjoBuey } from '#components/mobile/ayuda/OjoBuey';
import { usePreguntasMovimientos } from '#components/mobile/ayuda/usePreguntasMovimientos';
import { useCategoriasExcluidas } from '#components/mobile/informes/useCategoriasExcluidas';
import { useTotalesMensuales } from '#components/mobile/informes/useTotalesMensuales';
import { Cabecera } from '#components/mobile/ui/Cabecera';
import { color, num } from '#components/mobile/ui/tokens';
import { Page } from '#components/Page';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useFormat } from '#hooks/useFormat';
import { useLocale } from '#hooks/useLocale';
import { useSyncedPref } from '#hooks/useSyncedPref';

import { gastadoEnMes } from './gastadoEnMes';

/**
 * Pestaña «Movimientos» (antes «Gastos», H59): los movimientos de todas las
 * cuentas (la vista «Todas las cuentas» de Actual). «Buscar» está en la
 * brújula; «Añadir movimiento» lo pone la brújula en todas las pantallas.
 * El ojo de buey de la cabecera abre la ayuda con Gavi.
 * La ruta sigue siendo `/gastos` para no romper enlaces.
 */
export function GastosPage() {
  const { t } = useTranslation();
  const [numberFormat] = useSyncedPref('numberFormat');
  const [hideFraction] = useSyncedPref('hideFraction');
  const preguntasAyuda = usePreguntasMovimientos();
  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  return (
    <Page
      header={
        <Cabecera
          titulo={
            <span data-ayuda="titulo-movimientos">
              <Trans>Transactions</Trans>
            </span>
          }
          subtitulo={t('All accounts')}
          derecha={<OjoBuey onPress={() => setAyudaAbierta(true)} />}
          style={{ paddingBottom: 10 }}
        />
      }
      padding={0}
    >
      {/* La clave fuerza a repintar la lista si cambia el formato numérico. */}
      <Fragment key={`${numberFormat}${hideFraction}`}>
        <AllAccountTransactions cabecera={<GastadoEsteMes />} />
      </Fragment>
      {ayudaAbierta && (
        <AyudaGavi
          pantalla={t('Transactions')}
          preguntas={preguntasAyuda}
          onCerrar={() => setAyudaAbierta(false)}
        />
      )}
    </Page>
  );
}

/**
 * «Gastado en octubre: 1.234,56 €» en lugar del saldo total (H59): la misma
 * cifra que Informes → Gastos e Inicio → Gasto del mes (mismo filtro «qué
 * cuenta como gasto»).
 */
export function GastadoEsteMes() {
  const { t } = useTranslation();
  const format = useFormat();
  const locale = useLocale();
  const mes = monthUtils.currentMonth();
  const { excluidas } = useCategoriasExcluidas();
  const { movimientos, categorias, isLoading } = useTotalesMensuales({
    meses: 1,
    hasta: mes,
    categoriasExcluidas: excluidas,
  });
  const total = gastadoEnMes(movimientos, mes, categorias);

  return (
    <View
      data-testid="movimientos-gastado-mes"
      style={{
        flexDirection: 'row',
        alignItems: 'baseline',
        justifyContent: 'center',
        gap: 6,
        maxWidth: '100%',
        paddingTop: 2,
        paddingBottom: 2,
      }}
    >
      <Text style={{ fontSize: 14, fontWeight: 700, color: color.fg3 }}>
        {t('Spent in {{month}}:', {
          month: monthUtils.format(mes, 'MMMM', locale),
        })}
      </Text>
      <PrivacyFilter>
        <Text
          style={{ ...num, fontSize: 17, fontWeight: 800, color: color.fg }}
        >
          {isLoading ? '…' : format(total, 'financial')}
        </Text>
      </PrivacyFilter>
    </View>
  );
}
